import React from 'react';
import { Alert, Button, Space, Steps } from 'antd';
import { useImportJob } from '../../hooks/useImportJob';
import { ImportJobView } from '../../services/importJobApi';
import { IMPORT_STEPS, importJobStepIndex } from '../../utils/importJobView';

export interface ImportJobProgressProps {
    importId: string;
    /** Fires once, when the database and the bot both hold the import. */
    onReady?: (job: ImportJobView) => void;
    /** Hide the step bar: a one-line banner. */
    compact?: boolean;
}

/** Where an import job is, with Retry / Cancel when it failed a step or stopped moving. */
const ImportJobProgress: React.FC<ImportJobProgressProps> = ({ importId, onReady, compact = false }) => {
    const job = useImportJob(importId, onReady);
    const { status } = job;

    const alertType =
        status === 'READY' ? 'success'
        : status === 'FAILED' ? 'error'
        : status === 'CANCELLED' ? 'info'
        : job.error || job.stuck ? 'warning'
        : 'info';

    const detail = !job.final && job.error
        ? job.error
        : job.stuck
            ? 'No progress for over 2 minutes. The server retries on its own; you can also retry now or cancel.'
            : status === 'CANCELLED'
                ? 'Nothing was changed: the database was put back as it was.'
                : null;

    return (
        <div data-testid="import-job-progress" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {!compact && status && status !== 'CANCELLED' && status !== 'CANCELLING' && (
                <Steps
                    size="small"
                    current={Math.min(importJobStepIndex(status), IMPORT_STEPS.length - 1)}
                    status={status === 'FAILED' ? 'error' : status === 'READY' ? 'finish' : 'process'}
                    items={IMPORT_STEPS.map((title) => ({ title }))}
                />
            )}
            <Alert
                type={alertType}
                showIcon
                style={{ fontSize: 12 }}
                message={`Import: ${job.label}`}
                description={
                    detail || job.actionError || job.canRetry || job.canCancel ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                            {detail && <span>{detail}</span>}
                            {job.actionError && <span style={{ color: '#F6465D' }}>{job.actionError}</span>}
                            {(job.canRetry || job.canCancel) && (
                                <Space wrap>
                                    {job.canRetry && (
                                        <Button size="small" loading={job.acting === 'retry'} disabled={job.acting != null} onClick={() => void job.retry()}>
                                            Retry
                                        </Button>
                                    )}
                                    {job.canCancel && (
                                        <Button size="small" danger loading={job.acting === 'cancel'} disabled={job.acting != null} onClick={() => void job.cancel()}>
                                            Cancel import
                                        </Button>
                                    )}
                                </Space>
                            )}
                        </div>
                    ) : undefined
                }
            />
        </div>
    );
};

export default ImportJobProgress;
